import { useEffect, useState } from "react";
import {
  BooleanParam,
  createEnumParam,
  JsonParam,
  type QueryParamConfigMap,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import useAllLabels from "features/labels/datahooks/useAllLabels";
import {
  MobileHeading,
  NavTabsContentContainer,
  TableAndFiltersContainer,
} from "features/layout/components";
import { LeadPreviewSidebar } from "features/leads/components";

import { ColumnsVisibilityControl } from "common/components/DataTable";
import useColumnVisibility from "common/components/DataTable/useColumnVisibility";
import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import PaginationFooter from "common/components/PaginationFooter";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { DEFAULT_LEADS_SEARCH_PARAMS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useLeadsInfo from "common/datahooks/useLeadsInfo";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import { typedObjectEntries } from "common/helpers/utils";
import useLeadsSearchParams from "common/hooks/useLeadsSearchParams";
import usePagination from "common/hooks/usePagination";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  FacetParam,
  Lead,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

import { LEADS_COLUMNS, LEADS_TAB_ITEMS } from "../../constants";
import useLeads from "../../datahooks/useLeads";
import DesktopFilters from "./DesktopLayout/DesktopFilters";
import DesktopTable from "./DesktopLayout/DesktopTable";
import ExportLeads from "./ExportLeads";
import KeywordSearchInput from "./KeywordSearchInput";
import LeadsEmptyPlaceholder from "./LeadsEmptyPlaceholder";
import ManageLeads from "./MobileLayout/ManageLeads";
import MobileFilters from "./MobileLayout/MobileFilters";
import MobileList from "./MobileLayout/MobileList";

import type { JSX } from "react";

import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";
import SyncingLeadsImage from "assets/images/empty-placeholders/syncing-leads.png";

const paramsConfig = typedObjectEntries(DEFAULT_LEADS_SEARCH_PARAMS).reduce(
  (accumulator, [key, value]) => {
    if (typeof value === "boolean") {
      accumulator[key] = withDefault(BooleanParam, value);
    } else if (typeof value === "string") {
      accumulator[key] = withDefault(StringParam, value);
    } else {
      accumulator[key] = withDefault(JsonParam, value);
    }
    return accumulator;
  },
  {} as QueryParamConfigMap,
);

export default function AllLeads() {
  const isDesktop = useTwBreakpoint("lg");
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const [searchParams, setSearchParams] = useQueryParams(paramsConfig, {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  }) as [
    LeadsSearchParams<FacetParam>,
    (searchParams: Partial<LeadsSearchParams<SelectedFacetParam>>) => void,
  ];
  const { allLabels } = useAllLabels("workspace");
  const { workspaceAccounts } = useWorkspaceAccounts(workspaceId);

  //remove labels from search params if they are missing
  useEffect(() => {
    if (allLabels && searchParams.labels.length) {
      setSearchParams({
        labels: searchParams.labels.filter(
          (param) => allLabels.findIndex(({ id }) => id === param.id) !== -1,
        ),
      });
    }
  }, [allLabels]);

  //remove accounts from search params if they are missing
  useEffect(() => {
    if (workspaceAccounts && searchParams.lead_of.length) {
      setSearchParams({
        lead_of: searchParams.lead_of.filter(
          (param) =>
            workspaceAccounts.findIndex(({ id }) => id === param.id) !== -1,
        ),
      });
    }
  }, [workspaceAccounts]);

  const [orderSort] = useQueryParam<"asc" | "desc">(
    "order",
    withDefault(createEnumParam(["asc", "desc"]), "asc"),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  const [leadPreviewState, setLeadPreviewState] = useState<{
    isOpen: boolean;
    leadId: null | string;
    action: "preview" | "notes" | null;
  }>({
    isOpen: false,
    leadId: null,
    action: null,
  });
  const [selectedLeads, setSelectedLeads] = useState<Lead[]>([]);
  const [keywordInputValue, setKeywordInputValue] = useState(
    searchParams.keywords,
  );

  function onSearchParamsChange(
    searchParams: Partial<LeadsSearchParams<SelectedFacetParam>>,
  ) {
    setPage(0);
    setLeadPreviewState({ ...leadPreviewState, isOpen: false });
    setSearchParams(searchParams);
  }

  const { hasFilters, resetAllParams, resetAllFilters, setSearchParam } =
    useLeadsSearchParams(searchParams, onSearchParamsChange);

  const { page, setPage, pageSize, setPageSize } = usePagination("leads");

  const { columnVisibility, setColumnVisibility } = useColumnVisibility(
    "leads",
    LEADS_COLUMNS,
  );

  const { leadsInfo, isFetchingLeadsInfo, leadsInfoError, refetchLeadsInfo } =
    useLeadsInfo(searchParams);
  const { leads, isFetchingLeads, leadsError, refetchLeads } = useLeads(
    searchParams,
    {
      page,
      order: orderSort,
      pageSize,
    },
  );

  useEffect(() => {
    setSelectedLeads([]);
  }, [searchParams]);

  //re-sync selected leads to the latest data so eligibility (e.g. favorite/unfavorite) recomputes against fresh state
  useEffect(() => {
    if (leads) {
      setSelectedLeads((prevSelectedLeads) =>
        prevSelectedLeads.length
          ? prevSelectedLeads.map(
              (selectedLead) =>
                leads.find(({ id }) => id === selectedLead.id) ?? selectedLead,
            )
          : prevSelectedLeads,
      );
    }
  }, [leads]);

  useEffect(() => {
    setLeadPreviewState({ ...leadPreviewState, isOpen: false });
  }, [page]);

  function openPreview(leadId: string, action: "preview" | "notes") {
    if (action === "preview") {
      if (leadPreviewState.isOpen && leadId === leadPreviewState.leadId) {
        setLeadPreviewState({ ...leadPreviewState, isOpen: false });
      } else {
        setLeadPreviewState({ leadId, isOpen: true, action: "preview" });
      }
    } else {
      setLeadPreviewState({ leadId, isOpen: true, action: "notes" });
    }
  }

  const isEmpty =
    !isFetchingLeads &&
    (!leads || !leads.length) &&
    !hasFilters &&
    !searchParams.keywords;

  let mainContent: JSX.Element;
  const searchInputComponent = (
    <KeywordSearchInput
      setKeywordSearchParam={(keywordSearchParam) =>
        setSearchParam("keywords", keywordSearchParam)
      }
      keywordInputValue={keywordInputValue}
      setKeywordInputValue={setKeywordInputValue}
    />
  );
  const exportLeadsComponent = (
    <RenderIf condition={!isEmpty}>
      <ExportLeads
        searchParams={searchParams}
        totalLeadsCount={leadsInfo?.totalLeads ?? 0}
        isFetching={isFetchingLeadsInfo}
      />
    </RenderIf>
  );

  if (leadsError || leadsInfoError) {
    mainContent = (
      <ErrorPlaceholder
        errorMessage="Could not display leads data"
        onRetry={() => {
          if (leadsInfoError) {
            refetchLeadsInfo();
          }
          if (leadsError) {
            refetchLeads();
          }
        }}
      />
    );
  } else {
    if (
      leadsInfo &&
      Object.values(leadsInfo.accountsSync).every((isSyncing) => !!isSyncing)
    ) {
      mainContent = (
        <EmptyPlaceholder
          imageSrc={SyncingLeadsImage}
          title="Your Leads are loading"
          subtitle="Leads are currently synchronizing with your LinkedIn account. This process can take up to 24 hours, depending on your number of connections"
        />
      );
    } else if (isEmpty) {
      mainContent = <LeadsEmptyPlaceholder />;
    } else {
      const commonProps = {
        leads,
        selectedLeads,
        setSelectedLeads,
        isLoading: isFetchingLeads,
        pageSize,
        totalItemCount: leadsInfo ? leadsInfo.totalLeads : 0,
      };

      let content = (
        <EmptyPlaceholder
          title="No leads found"
          subtitle="There were no results that match your criteria"
          imageSrc={NoResultsImage}
          actionComponent={
            <Button
              onClick={() => {
                setKeywordInputValue("");
                resetAllParams();
              }}
            >
              Clear
            </Button>
          }
        />
      );

      if (isFetchingLeads || leads?.length) {
        content = (
          <>
            {isDesktop ? (
              <DesktopTable
                {...commonProps}
                openSidebar={openPreview}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
              />
            ) : (
              <MobileList {...commonProps} />
            )}
            {!leadsInfoError && (
              <PaginationFooter
                pageSize={pageSize}
                setPageSize={setPageSize}
                selectedPage={page}
                setSelectedPage={setPage}
                totalItemCount={commonProps.totalItemCount}
                isLastPageHidden
              />
            )}
          </>
        );
      }

      mainContent = isDesktop ? (
        <TableAndFiltersContainer
          searchComponent={searchInputComponent}
          filtersComponent={
            <DesktopFilters
              searchParams={searchParams}
              setSearchParams={onSearchParamsChange}
              clearAllFilters={hasFilters && resetAllFilters}
            />
          }
          columnSelectionComponent={
            <ColumnsVisibilityControl
              columns={LEADS_COLUMNS}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
            />
          }
        >
          {content}
        </TableAndFiltersContainer>
      ) : (
        <>
          <MobileHeading
            actionComponent={
              <>
                {exportLeadsComponent}
                <RenderIf condition={!!selectedLeads.length}>
                  <ManageLeads selectedLeads={selectedLeads} />
                </RenderIf>
              </>
            }
            searchComponent={
              <>
                {searchInputComponent}
                <MobileFilters
                  searchParams={searchParams}
                  setSearchParams={onSearchParamsChange}
                />
              </>
            }
          />
          {content}
        </>
      );
    }
  }

  return (
    <div className="flex h-full grow gap-x-2 min-h-0">
      <NavTabsContentContainer
        desktopActionComponent={exportLeadsComponent}
        tabItems={LEADS_TAB_ITEMS}
      >
        {mainContent}
      </NavTabsContentContainer>
      {leadPreviewState.leadId && (
        <LeadPreviewSidebar
          initialState={
            leadPreviewState.action === "notes"
              ? leads.find(({ id }) => id === leadPreviewState.leadId)
              : leadPreviewState.leadId
          }
          isOpen={leadPreviewState.isOpen}
          onClose={() =>
            setLeadPreviewState({ ...leadPreviewState, isOpen: false })
          }
        />
      )}
    </div>
  );
}
